// --- React & Redux
import React, { useRef, useMemo, forwardRef, useImperativeHandle } from "react";
import { useSelector } from "react-redux";
import moment from "moment-timezone";
// --- External UI Libraries
import DataGrid from "@dtci/spellbook/DataGrid";
// --- Config
import {
  formatDateTime,
  formatTapOfferStatus,
  COLUMN_OPTIONS,
  FILTER_COLUMNS,
  getOfferHistoryExcelExportColumnDefs,
} from "./config/columns";
import { offerHistory as offerHistoryColumns } from "../../../../components/AGGrid/config/columns-def";
// --- Hooks & Actions
import { useOfferHistoryActions } from "tap/store/offerHistory/useOfferHistory.js";
// --- Feature flag utility
import { hasFeature } from "tap/utils/permissions.js";
import { escapeHtml } from "tap/utils/htmlEscape.js";
// --- Excel Export
import ExcelJS from "exceljs";
import FileSaver from "file-saver";
// --- Styles
import styles from "../../../../components/AGGrid/details-table.module.scss";

/**
 * Save buffer as Excel file
 */
const saveAsExcelFile = (buffer, fileName) => {
  const EXCEL_TYPE =
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
  const EXCEL_EXTENSION = ".xlsx";
  const data = new Blob([buffer], { type: EXCEL_TYPE });
  FileSaver.saveAs(data, fileName + EXCEL_EXTENSION);
};

/**
 * Export data to Excel with ordered columns and optional per-cell value getters.
 * @param {{ field: string, header: string, getValue?: (row: object) => unknown }[]} columnDefs
 */
const exportAsExcelFile = async (data, excelFileName, columnDefs) => {
  if (!data || data.length === 0) {
    console.warn("No data to export");
    return;
  }

  if (!columnDefs || columnDefs.length === 0) {
    console.warn("No column definitions for export");
    return;
  }

  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet("OfferHistory");

  sheet.columns = columnDefs.map(({ field, header }) => ({
    header,
    key: field,
  }));

  for (let i = 0; i < data.length; i++) {
    const dataRow = data[i];
    const rowObj = {};
    for (let j = 0; j < columnDefs.length; j++) {
      const def = columnDefs[j];
      const { field, getValue } = def;
      rowObj[field] =
        typeof getValue === "function" ? getValue(dataRow) : dataRow[field];
    }
    sheet.addRow(rowObj);
  }

  try {
    const buffer = await workbook.xlsx.writeBuffer();
    saveAsExcelFile(buffer, excelFileName);
  } catch (error) {
    console.error("Error writing Excel buffer:", error);
  }
};

/**
 * Apply searchAll filter to history data
 */
const applySearchFilter = (data, searchAll) => {
  if (!data || !Array.isArray(data)) return [];
  if (!searchAll || searchAll.trim() === "") return data;

  const lowerSearch = searchAll.toLowerCase().trim();
  return data.filter((row) => {
    return Object.values(row).some((value) => {
      if (value === null || value === undefined) return false;
      return String(value).toLowerCase().includes(lowerSearch);
    });
  });
};

/**
 * Apply column filters to data
 */
const applyColumnFilters = (data, internalFilters) => {
  if (!data || !Array.isArray(data)) return [];
  if (!internalFilters) return data;

  const activeFilters = Object.entries(internalFilters).filter(
    ([, values]) => values && values.length > 0,
  );

  if (activeFilters.length === 0) return data;

  return data.filter((row) => {
    return activeFilters.every(([field, selectedValues]) => {
      const cellValue = row[field];
      const cellStr =
        cellValue !== null && cellValue !== undefined ? String(cellValue) : "";
      return selectedValues.some((opt) => opt.value === cellStr);
    });
  });
};

/**
 * Get display label for a field value
 */
const getFieldLabel = (field, value) => {
  if (field === "tapOfferStatus") {
    return formatTapOfferStatus(value);
  }
  if (field === "needsAudit" || field === "readyToPublish") {
    return value === "true" || value === true ? "Yes" : "No";
  }
  return value;
};

/**
 * Build filter options from data for each filterable column
 */
const buildFilterOptions = (data, filterColumns) => {
  const options = {};
  filterColumns.forEach((col) => {
    const uniqueValues = new Set();
    data?.forEach((row) => {
      const value = row[col.field];
      if (value !== null && value !== undefined && value !== "") {
        uniqueValues.add(String(value));
      }
    });
    options[col.field] = Array.from(uniqueValues)
      .sort((a, b) => {
        const labelA = getFieldLabel(col.field, a);
        const labelB = getFieldLabel(col.field, b);
        // Check if both values are numeric
        const numA = parseFloat(labelA);
        const numB = parseFloat(labelB);
        if (!isNaN(numA) && !isNaN(numB)) {
          return numA - numB;
        }
        // Alphabetical sort A-Z
        return labelA.localeCompare(labelB);
      })
      .map((val) => ({ value: val, label: getFieldLabel(col.field, val) }));
  });
  return options;
};

/**
 * Detail Cell Renderer for Master Detail - displays the message field
 */
const MessageDetailCellRenderer = forwardRef((props, ref) => {
  const { data } = props;
  const message = data?.message;

  React.useImperativeHandle(ref, () => ({
    refresh: () => true,
  }));

  return (
    <div className={styles.detailPanel}>
      <div className={styles.detailLabel}>Message</div>
      {message ? (
        <div
          className={styles.detailMessage}
          style={{ whiteSpace: "pre-wrap" }}
        >
          {message}
        </div>
      ) : (
        <div className={styles.detailEmpty}>No message available</div>
      )}
    </div>
  );
});

/**
 * Offer History Grid
 *
 * Displays history records for a single offer in a read-only grid.
 * Shows change highlighting between consecutive history entries.
 *
 * @param {object} props
 * @param {Array} props.historyData - Array of history records
 * @param {object} props.selectedOffer - The selected offer object
 * @param {string} props.searchAll - Search text to filter across all fields
 * @param {Array} props.hiddenColumns - Array of hidden column field names
 * @param {React.Ref} ref - Ref to expose grid actions
 */
export const OfferHistoryGrid = forwardRef(
  (
    {
      historyData = [],
      selectedOffer = null,
      searchAll = "",
      hiddenColumns = [],
    },
    ref,
  ) => {
    const gridApiRef = useRef(null);

    const { internalFilters } = useSelector((state) => state.offerHistory);
    const { updateOfferHistoryFilters, setOfferHistoryFilters } =
      useOfferHistoryActions();

    const hasEpisodeFeature = hasFeature(
      "TAP_OFFER_EPISODE_TIER_AND_EPISODE_EMA_PRICE_TYPE",
    );

    const columns = useMemo(() => {
      const colConfig = offerHistoryColumns();
      const baseColumns = colConfig.default;

      if (hasEpisodeFeature) {
        // Insert episode columns after seasonPassAmt (before pmxPlatform)
        const insertIndex = baseColumns.findIndex(
          (c) => c.field === "pmxPlatform",
        );
        if (insertIndex > -1) {
          return [
            ...baseColumns.slice(0, insertIndex),
            ...colConfig.withEpisodeFeature,
            ...baseColumns.slice(insertIndex),
          ];
        }
        // Fallback: append at end if pmxPlatform not found
        return [...baseColumns, ...colConfig.withEpisodeFeature];
      }

      return baseColumns;
    }, [hasEpisodeFeature]);

    const dataWithIndices = useMemo(() => {
      if (!historyData || !Array.isArray(historyData)) return [];
      return historyData.map((row, index) => ({
        ...row,
        _originalIndex: index,
      }));
    }, [historyData]);

    // Build filter options from data
    const filterOptions = useMemo(() => {
      return buildFilterOptions(dataWithIndices, FILTER_COLUMNS);
    }, [dataWithIndices]);

    // Apply searchAll and column filters (preserves _originalIndex)
    const filteredData = useMemo(() => {
      let result = applySearchFilter(dataWithIndices, searchAll);
      result = applyColumnFilters(result, internalFilters);
      return result;
    }, [dataWithIndices, searchAll, internalFilters]);

    // Context passed to grid for cell renderers to access original data
    const gridContext = useMemo(
      () => ({
        originalData: dataWithIndices,
      }),
      [dataWithIndices],
    );

    // Calculate active filter count
    const activeFilterCount = useMemo(() => {
      return Object.values(internalFilters).filter((v) => v && v.length > 0)
        .length;
    }, [internalFilters]);

    // Filter handlers
    const handleFilterChange = (field, values) => {
      const offerId = selectedOffer?.id;
      updateOfferHistoryFilters({
        [field]: values,
        offerId,
      });
    };

    const handleClearFilters = () => {
      const offerId = selectedOffer?.id;
      setOfferHistoryFilters({}, offerId);
    };

    const handleExport = async () => {
      if (!filteredData || filteredData.length === 0) {
        console.warn("No data to export");
        return;
      }

      const exportColumns =
        getOfferHistoryExcelExportColumnDefs(hasEpisodeFeature);

      const fileName = selectedOffer?.id
        ? `OfferHistory_${selectedOffer.id}_${moment().format("YYYY-MM-DD")}`
        : `OfferHistory_${moment().format("YYYY-MM-DD")}`;

      try {
        await exportAsExcelFile(filteredData, fileName, exportColumns);
      } catch (error) {
        console.error("Export failed:", error);
      }
    };

    const handlePrint = () => {
      if (!filteredData || filteredData.length === 0) {
        return;
      }

      const visibleColumns = columns.filter((col) => !col.hide);

      const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Offer History - Offer ${escapeHtml(selectedOffer?.id || "")}</title>
          <style>
            body { font-family: Arial, sans-serif; font-size: 10px; margin: 20px; }
            h1 { font-size: 16px; margin-bottom: 10px; }
            .info { font-size: 11px; color: #666; margin-bottom: 15px; }
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid #ddd; padding: 4px 6px; text-align: left; }
            th { background-color: #f5f5f5; font-weight: bold; }
            tr:nth-child(even) { background-color: #fafafa; }
            @media print {
              body { margin: 0; }
              table { font-size: 9px; }
            }
          </style>
        </head>
        <body>
          <h1>Offer History${selectedOffer?.id ? ` - Offer ${escapeHtml(selectedOffer.id)}` : ""}</h1>
          <div class="info">
            Printed: ${moment().format("YYYY-MM-DD HH:mm")} | 
            Total Records: ${escapeHtml(filteredData.length)}
            ${searchAll ? ` | Search: "${escapeHtml(searchAll)}"` : ""}
          </div>
          <table>
            <thead>
              <tr>
                ${visibleColumns.map((col) => `<th>${escapeHtml(col.headerName)}</th>`).join("")}
              </tr>
            </thead>
            <tbody>
              ${filteredData
                .map(
                  (row) => `
                <tr>
                  ${visibleColumns
                    .map((col) => {
                      let value = row[col.field];
                      if (
                        col.field === "historyCreatedAt" ||
                        col.field === "modifiedAt"
                      ) {
                        value = formatDateTime(value);
                      }
                      if (col.field === "historyCreatedBy") {
                        value = row.historyCreatedBy?.fullName || "";
                      }
                      if (col.field === "tapOfferStatus") {
                        value = formatTapOfferStatus(value);
                      }
                      return `<td>${escapeHtml(value)}</td>`;
                    })
                    .join("")}
                </tr>
              `,
                )
                .join("")}
            </tbody>
          </table>
        </body>
      </html>
    `;

      const printWindow = window.open("", "_blank");
      if (printWindow) {
        printWindow.document.write(printContent);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
      }
    };

    // Filter columns by visibility
    const visibleColumns = useMemo(() => {
      const filtered = columns.filter((col) => {
        if (!col.field) return true;
        return !hiddenColumns.includes(col.field);
      });

      const expandColumn = {
        headerName: "",
        field: "expand",
        width: 50,
        cellRenderer: "agGroupCellRenderer",
        suppressSizeToFit: true,
        resizable: false,
        sortable: false,
        pinned: "left",
      };

      return [expandColumn, ...filtered];
    }, [columns, hiddenColumns]);

    // Expose methods and data to parent via ref
    useImperativeHandle(
      ref,
      () => ({
        handleExport,
        handlePrint,
        // Filter panel data
        filterOptions,
        internalFilters,
        handleFilterChange,
        handleClearFilters,
        activeFilterCount,
        FILTER_COLUMNS,
        // Column options (for drawer)
        COLUMN_OPTIONS,
      }),
      [filterOptions, internalFilters, activeFilterCount, hasEpisodeFeature],
    );

    return (
      <div className={styles.gridContainer}>
        <div className={styles.gridMain}>
          <DataGrid
            className={styles.dataGrid}
            dataGridApiRef={gridApiRef}
            agGrid={{
              rowData: filteredData ?? [],
              headerHeight: 45,
              suppressColumnStateReset: true,
              context: gridContext,
              // Master Detail configuration for message
              masterDetail: true,
              detailRowAutoHeight: true,
              detailCellRenderer: MessageDetailCellRenderer,
              isRowMaster: (dataItem) =>
                dataItem?.message != null && dataItem?.message !== "",
            }}
            columnDefs={visibleColumns}
            defaultColumnDef={{
              wrapText: true,
              autoHeight: true,
              sortable: true,
              resizable: true,
              suppressHeaderMenuButton: true,
              cellStyle: {
                paddingTop: "10px",
                paddingBottom: "10px",
                wordBreak: "break-word",
              },
            }}
            bordered="none"
            striped
            autoSizing
            size="default"
            sortable
            noResultsMessage="No History Found"
          />
        </div>
      </div>
    );
  },
);
